import React, { useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import WebhookConfigService from '../services/WebhookConfigService';
import { toast } from 'react-toastify';
import { Row, Form, Col, Input } from 'reactstrap';
import ReactButton from 'components/Buttons/ReactButton';
import DefaultModal from 'components/Modal/DefaultModal';
import FlexSelect from 'components/Inputs/FlexSelect';
import { FiMessageSquare } from 'react-icons/fi';
import { MdOutlineDescription } from 'react-icons/md';

const CreateAndEditWebhookConfig = ({
  isModalOpen,
  onModalToggle,
  WebhookConfigFromUpdate,
  getWebhookConfig = () => {},
  ...props
}) => {
  const closeModal = useCallback(() => onModalToggle(false), [onModalToggle]);
  const { control, errors, watch, handleSubmit, setValue } = useForm();

  const eventOptionsRaw = [
    { id: 'conversa_encerrada', name: 'Conversa Encerrada', category: 'CHAT' },
    { id: 'conversa_iniciada', name: 'Conversa Iniciada', category: 'CHAT' },
    {
      id: 'conversa_transferida_para_atendente',
      name: 'Conversa Transferida',
      category: 'CHAT'
    },
    {
      id: 'atendente_enviou_mensagem',
      name: 'Atendente Enviar Mensagem',
      category: 'CHAT'
    },
    {
      id: 'cliente_enviou_mensagem',
      name: 'Cliente Enviar Mensagem',
      category: 'CHAT'
    },

    { id: 'protocolo_criado', name: 'Protocolo Criado', category: 'PROTOCOL' },
    {
      id: 'protocolo_comentario_adicionado',
      name: 'Protocolo - Comentário Público Adicionado',
      category: 'PROTOCOL'
    },
    {
      id: 'protocolo_encerrado',
      name: 'Protocolo Encerrado',
      category: 'PROTOCOL'
    }
  ];

  const processEventOptions = () => {
    const categories = [];

    return eventOptionsRaw.map((event) => {
      let icon;
      let categoryLabel;
      let isDivisor = false;

      if (!categories.includes(event.category)) {
        categories.push(event.category);
        isDivisor = true;
      }

      switch (event.category) {
        case 'CHAT':
          icon = <FiMessageSquare style={{ margin: '0px 0.5rem 0px 0px' }} />;
          categoryLabel = 'Ações de Chat';
          break;
        case 'PROTOCOL':
          icon = (
            <MdOutlineDescription style={{ margin: '0px 0.5rem 0px 0px' }} />
          );
          categoryLabel = 'Ações de Protocolo';
          break;
        default:
          break;
      }

      return {
        id: event.id,
        name: event.name,
        divisorCategory: isDivisor ? categoryLabel : undefined,
        icon
      };
    });
  };
  const onSubmit = async () => {
    try {
      const dto = {
        name: watch('name'),
        event: watch('event'),
        url_to_send: watch('url_to_send')
      };

      if (!Object.keys(WebhookConfigFromUpdate || {}).length) {
        return handlerCreateWebhookConfig(dto);
      }
      await updateWebhookConfig(dto);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        toast.error(err.response.data.message, {
          autoClose: 3000,
          closeOnClick: true
        });
      } else {
        toast.error('Erro ao criar WebHook', {
          autoClose: 3000,
          closeOnClick: true
        });
      }
    }
  };

  const handlerName = () => {
    return WebhookConfigFromUpdate?.id
      ? 'Editar WebHook'
      : 'Criar nova WebHook';
  };

  const updateWebhookConfig = async ({ name, event, url_to_send }) => {
    if (!WebhookConfigFromUpdate?.id) throw new Error('Id não informado');
    await WebhookConfigService.update(WebhookConfigFromUpdate?.id, {
      name,
      event,
      url_to_send
    });
    getWebhookConfig();
    toast.success('WebHook atualizada com sucesso', {
      autoClose: 3000,
      closeOnClick: true
    });
    closeModal();
  };

  const handlerCreateWebhookConfig = async ({ name, event, url_to_send }) => {
    await WebhookConfigService.create({ name, event, url_to_send });

    getWebhookConfig();

    toast.success('WebHook criada com sucesso', {
      autoClose: 3000,
      closeOnClick: true
    });
    closeModal();
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  return (
    <DefaultModal
      show={isModalOpen}
      onModalToggle={onModalToggle}
      name={handlerName()}
      showCloseButton={false}
      modalContainerClasses="modal-lg"
      {...props}
    >
      <>
        <Form className="needs-validation">
          <Row className="mb-2">
            <Col className="px-0">
              <Col sm="12" className="mb-2">
                <label className="form-control-label">Título</label>
                <Controller
                  render={(props) => (
                    <Input
                      value={props.value}
                      type="text"
                      onChange={props.onChange}
                      placeholder="Título do Webhook"
                    />
                  )}
                  control={control}
                  name="name"
                  defaultValue={WebhookConfigFromUpdate?.name || ''}
                  rules={{
                    required: 'Campo obrigatório'
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="px-0">
              <Col sm="12" className="mb-2">
                <label className="form-control-label">URL</label>
                <Controller
                  render={(props) => (
                    <Input
                      value={props.value}
                      type="text"
                      onChange={props.onChange}
                      placeholder=""
                    />
                  )}
                  control={control}
                  name="url_to_send"
                  defaultValue={WebhookConfigFromUpdate?.url_to_send || ''}
                  rules={{
                    required: 'Campo obrigatório',
                    validate: (value) =>
                      /^(http|https):\/\/[^ "]+$/.test(value) || 'URL inválida'
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="url_to_send"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="px-0">
              <Col sm="12" className="mb-2">
                <label className="form-control-label">Evento</label>
                <Controller
                  render={(props) => (
                    <FlexSelect
                      dataOptions={processEventOptions()}
                      isMulti={false}
                      value={props.value}
                      valueController={setValue}
                      fieldName="event"
                      labelName="name"
                      valueName="id"
                      isClearable={true}
                      closeMenuOnSelect={true}
                    />
                  )}
                  control={control}
                  defaultValue={WebhookConfigFromUpdate?.event || ''}
                  rules={{
                    required: 'Campo obrigatório'
                  }}
                  name="event"
                />

                <ErrorMessage
                  errors={errors}
                  name="event"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            </Col>
          </Row>
          <hr />
          <ReactButton btnColor="confirmation" onClick={handleSubmit(onSubmit)}>
            Salvar
          </ReactButton>
          <ReactButton btnColor="cancelation" onClick={closeModal}>
            Voltar
          </ReactButton>
        </Form>
      </>
    </DefaultModal>
  );
};

export default CreateAndEditWebhookConfig;
